import { useMonaco } from "@monaco-editor/react";
import { useCallback, useEffect } from "react";
import colors from "tailwindcss/colors";

import { listEntitiesShortnames } from "../../api";
import { splitCompletionHandler } from "../../utils/completion-handlers";
import {
  entityHoverProvider,
  liquidTemplateHoverProvider,
} from "../../utils/monaco-hover-provider";
import { entityPlaceholderRegex } from "../../utils/regex-definitions";
import {
  createLiquidSyntaxTokenProvider,
  createSemanticTokenProvider,
  liquidTemplateTokenLegend,
} from "../../utils/semantic-token-providers";

// Define global types for TypeScript
declare global {
  interface Window {
    _latestEntityList: string[];
    _liquidCompletionProviderRegistered: boolean;
    _refreshEntityList: () => void;
  }
}

window._latestEntityList = [];
window._refreshEntityList = (): void => {};
window._liquidCompletionProviderRegistered = false;

const fetchEntitiesFromAPI = async (): Promise<string[]> => {
  try {
    const response = await listEntitiesShortnames();
    return response.data || [];
  } catch (error) {
    console.error("error fetching entities:", error);
    return [];
  }
};

const MonacoSetupProvider = (): null => {
  const monaco = useMonaco();

  // called from monaco editors when they mount
  const refreshEntityList = useCallback(async () => {
    const updatedEntities = await fetchEntitiesFromAPI();
    window._latestEntityList = updatedEntities;
  }, []);

  useEffect(() => {
    window._refreshEntityList = refreshEntityList;
  }, [refreshEntityList]);

  useEffect(() => {
    if (!monaco) return;

    const closingPairs = [
      { close: '"', open: '"' },
      { close: "}", open: "{" },
      { close: "]", open: "[" },
      { close: ")", open: "(" },
      { close: "}}", open: "{{" },
      { close: ">>", open: "<<" },
    ];

    monaco.languages.setLanguageConfiguration("json", {
      autoClosingPairs: closingPairs,
      wordPattern: entityPlaceholderRegex,
    });

    monaco.languages.setLanguageConfiguration("plaintext", {
      autoClosingPairs: closingPairs,
    });

    // Register the semantic token provider for JSON and plaintext to highlight entity placeholders (<< >>) and Liquid templates ({{ }}) with detailed syntax coloring
    const liquidSyntaxProvider = createLiquidSyntaxTokenProvider(
      entityPlaceholderRegex,
    );
    monaco.languages.registerDocumentSemanticTokensProvider(
      "json",
      liquidSyntaxProvider,
    );
    monaco.languages.registerDocumentSemanticTokensProvider(
      "plaintext",
      liquidSyntaxProvider,
    );

    monaco.languages.registerDocumentSemanticTokensProvider(
      "liquid",
      createSemanticTokenProvider(
        [{ regex: entityPlaceholderRegex, tokenType: 0 }],
        liquidTemplateTokenLegend,
      ),
    );

    // Register miru-dark theme based on vs-dark
    monaco.editor.defineTheme("miru-dark", {
      base: "vs-dark",
      colors: {
        "editor.background": colors.zinc["900"],
        "editor.foreground": colors.gray[100],
        "editor.lineHighlightBorder": "#00000000",
        "editor.selectionBackground": "#404040",
        "editor.selectionHighlightBackground": "#00000000",
        "editor.selectionHighlightBorder": "#00000000",
        focusBorder: "#00000000",
        lineHighlightBackground: colors.gray[800] + "50",
      },
      inherit: true,
      rules: [
        { foreground: "#4FA3FF", token: "entityname" },
        { foreground: "#A855F7", token: "liquidtemplate" },
        // Liquid template syntax highlighting
        { foreground: "#CE9178", token: "liquidstring" }, // Strings - orange/peach
        { foreground: "#FFFFFF", token: "liquidvariable" }, // Variables - white
        { foreground: "#FFFFFF", token: "liquidfilter" }, // Filters - white
        { foreground: "#B5CEA8", token: "liquidnumber" }, // Numbers - light green
        { foreground: "#D4D4D4", token: "liquidoperator" }, // Operators - light gray
        { foreground: "#A855F7", token: "liquidbraces" }, // Braces {{ }} - purple
      ],
    });

    // completion handler needs to be registered only once otherwise we get duplicate suggestions
    if (!window._liquidCompletionProviderRegistered) {
      window._liquidCompletionProviderRegistered = true;
      // the completion handlers provide the intellisense for suggestions between {{ }}

      monaco.languages.registerCompletionItemProvider(
        ["json", "plaintext", "liquid"],
        splitCompletionHandler,
      );

      monaco.languages.registerHoverProvider(
        ["json", "plaintext", "liquid"],
        entityHoverProvider,
      );

      monaco.languages.registerHoverProvider(
        ["json", "plaintext", "liquid"],
        liquidTemplateHoverProvider,
      );

      // TODO: for now turn off inlay hints, but uncomment here to put them back in if you want to see the liquid template rendering next to the pill
      /*monaco.languages.registerInlayHintsProvider(
        ["json", "plaintext"],
        liquidTemplateInlayHintProvider,
      );*/
    }
  }, [monaco]);

  return null; // Just runs Monaco setup logic
};

export default MonacoSetupProvider;
